import { Box, useToast } from '@iheartjane/reefer';
import { Form, useForm } from '@iheartjane/reefer-hook-form';
import type { InfiniteData } from '@tanstack/react-query';
import { useQueryClient } from '@tanstack/react-query';
import type { RowSelectionState, SortingState } from '@tanstack/react-table';
import { useTable } from '@tanstack/react-table';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useLocation, useNavigate } from 'react-router';

import {
  fetchProductQueryKey,
  useFetchProducts,
  useUpdateProductVisibility,
} from '@jane/business-admin/data-access';
import { useHasPermissions, useStoreId } from '@jane/business-admin/hooks';
import { useProductsTable } from '@jane/business-admin/providers/ProductsTableProvider';
import type {
  MenuProductForProductsTable,
  ProductIndexResponse,
} from '@jane/business-admin/types';
import {
  EventNames,
  ModalNames,
  normalizePath,
  track,
} from '@jane/business-admin/util';
import { Permission } from '@jane/shared/auth';

import { BaseProductTable } from './BaseProductTable';
import { EmptyState } from './EmptyStates';
import { productTableFeatures } from './productTableFeatures';
import { buildPublishedAndHiddenColumns } from './PublishedAndHiddenColumns';
import { filterParams, sortParams } from './PublishedProductTable';

export const HiddenProductTable = ({
  columnFiltersParent,
  isBulkEditTable = false,
  selectedProducts,
  setSelectedProducts,
  setColumnFiltersParent,
  bulkEditModalOpen,
}: {
  bulkEditModalOpen: boolean;
  columnFiltersParent: any;
  isBulkEditTable?: boolean;
  selectedProducts: RowSelectionState;
  setColumnFiltersParent?: any;
  setSelectedProducts: any;
}) => {
  const userCanEditProducts = useHasPermissions([Permission.EditProducts]);
  const { setCanEditProducts } = useProductsTable();

  useEffect(() => {
    setCanEditProducts(userCanEditProducts ?? false);
  }, [userCanEditProducts]);

  const formMethods = useForm();

  const storeId = useStoreId();
  const toast = useToast();
  const [sorting, setSorting] = useState<SortingState>([]);

  const { pathname } = useLocation();
  const openUnpublishedModal = (row: MenuProductForProductsTable) => {
    track({
      event: EventNames.OpenedModal,
      modal_name: ModalNames.UpdateMenuProduct,
      url: normalizePath(pathname, storeId),
    });
    navigate(`${row.id}`);
  };

  const {
    data,
    isFetched,
    isFetching,
    isFetchingNextPage,
    fetchNextPage,
    hasNextPage,
  } = useFetchProducts({
    params: {
      status: 'hidden',
      ...filterParams(columnFiltersParent),
      ...sortParams(sorting),
    },
    storeId,
  });
  const {
    mutateAsync,
    isSuccess: visibilitySuccess,
    isError: visibilityError,
  } = useUpdateProductVisibility(storeId);

  const updateProductVisibility = useCallback(
    (productId: string, visible: boolean) => {
      mutateAsync({ data: { visible }, productId });
    },
    []
  );

  const totalRowCount = data?.pages?.[0]?.meta?.total ?? 0;

  const columns = useMemo(
    () =>
      buildPublishedAndHiddenColumns({
        isBulkEditTable,
        isFetched,
        updateProductVisibility,
        totalRowCount,
        sorting,
      }),
    [
      JSON.stringify(data),
      isBulkEditTable,
      isFetched,
      updateProductVisibility,
      totalRowCount,
      sorting,
    ]
  );

  const allRows = useMemo(
    () =>
      data?.pages
        ?.flatMap((page) => page.meta.all_product_ids)
        .reduce(
          (a, v) => ({
            ...a,
            [v.menu_product_id.toString()]: true,
          }),
          {}
        ) ?? {},
    [JSON.stringify(data)]
  );

  const productData = useMemo(
    () => data?.pages?.flatMap((page) => page.products) ?? [],
    [JSON.stringify(data)]
  );

  const queryClient = useQueryClient();
  const totalRowsFromCache =
    queryClient.getQueryData<InfiniteData<ProductIndexResponse>>(
      fetchProductQueryKey(storeId, {
        status: 'hidden',
      })
    )?.pages?.[0]?.meta?.total ?? 0;

  const reactTable = useTable({
    features: productTableFeatures,
    data: productData,
    columns,
    state: {
      columnFilters: columnFiltersParent,
      sorting,
      rowSelection: selectedProducts,
    },
    onColumnFiltersChange: setColumnFiltersParent,
    onSortingChange: setSorting,
    onRowSelectionChange: setSelectedProducts,
    enableMultiRowSelection: true,
    enableRowSelection: true,
    getRowId: (row) => row.id.toString(),
    manualSorting: true,
    manualFiltering: true,
  });

  useEffect(() => {
    if (visibilitySuccess) {
      toast.add({
        label: 'Product visibility successfully changed',
        variant: 'success',
      });
    }

    if (visibilityError) {
      toast.add({
        label: 'Product visibility change failed',
        variant: 'error',
      });
    }
  }, [visibilitySuccess, visibilityError]);

  useEffect(() => {
    if (!bulkEditModalOpen) {
      setColumnFiltersParent([]);
    }
  }, [storeId]);

  const navigate = useNavigate();
  const handleRowClick = (row: any) => {
    if (isBulkEditTable || userCanEditProducts === false) {
      return;
    }

    openUnpublishedModal(row);
  };

  return (
    <Box height="100%">
      {isFetched && totalRowsFromCache === 0 ? (
        <EmptyState variant="hidden" />
      ) : (
        <Form.BaseForm
          formMethods={formMethods}
          onSubmit={() => null}
          name="test"
        >
          <BaseProductTable
            allIds={allRows}
            table={reactTable}
            productData={productData}
            fetchNextPage={fetchNextPage}
            isBulkEditTable={isBulkEditTable}
            isFetched={isFetched}
            isFetching={isFetching && !isFetchingNextPage}
            handleRowClick={handleRowClick}
            hasNextPage={hasNextPage || false}
            tableType="hidden"
          />
        </Form.BaseForm>
      )}
    </Box>
  );
};
